import { Query, skipToken, useQuery, useQueryClient } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { ApiError, OurUseQueryOptions } from "@nens/react-query-utils";
import { SchematisationRevision, getHCCApi } from "@nens/threedi/openapi-client";

import { createHccUpdateForQueries } from "./hooks/create-updater-for-queries";
import { fetchAllPagesHcc } from "./utils/fetch-all-pages";

const PENDING_STATUS_POLL_INTERVAL = 3000;

// isValid is null while the schematisation/revision check is still running.
const revisionIsBeingChecked = (revision: SchematisationRevision | null | undefined): boolean =>
  revision?.isValid == null;

const getHccRevisionQuery = (
  schematisationId: number | undefined,
  revisionId: number | undefined
): OurUseQueryOptions<SchematisationRevision> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-revision", schematisationId, revisionId] as const,
    queryFn:
      schematisationId !== undefined && revisionId !== undefined
        ? () =>
            getHCCApi().schematisationsRevisionsRead({
              id: revisionId,
              schematisationPk: schematisationId,
            })
        : skipToken,
    // keep polling until the schematisation check resolves.
    refetchInterval: (query: Query<SchematisationRevision, ApiError, SchematisationRevision>) =>
      revisionIsBeingChecked(query.state.data) && PENDING_STATUS_POLL_INTERVAL,
    refetchOnMount: (query: Query<SchematisationRevision, ApiError, SchematisationRevision>) =>
      revisionIsBeingChecked(query.state.data) ? "always" : true,
  });
};

const getHccLatestRevisionQuery = (
  id: number | undefined
): OurUseQueryOptions<SchematisationRevision> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-latest-revision", id] as const,
    queryFn: id !== undefined ? () => getHCCApi().schematisationsLatestRevision({ id }) : skipToken,
    // keep polling until the schematisation check resolves.
    refetchInterval: (query: Query<SchematisationRevision, ApiError, SchematisationRevision>) =>
      revisionIsBeingChecked(query.state.data) && PENDING_STATUS_POLL_INTERVAL,
    refetchOnMount: (query: Query<SchematisationRevision, ApiError, SchematisationRevision>) =>
      revisionIsBeingChecked(query.state.data) ? "always" : true,
  });
};

export const useHccLatestRevisionQuery = (id: number | undefined) =>
  useQuery(getHccLatestRevisionQuery(id));

type HccRevisionsList = {
  count: number;
  results: SchematisationRevision[];
};

const getHccRevisionsListQuery = (
  schematisationId: number | undefined
): OurUseQueryOptions<HccRevisionsList> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-revisions-list", schematisationId] as const,
    queryFn:
      schematisationId !== undefined
        ? () =>
            fetchAllPagesHcc((pageArgs) =>
              getHCCApi().schematisationsRevisionsList({
                ...pageArgs,
                schematisationPk: schematisationId,
                committed: true,
              })
            )
        : skipToken,
  });
};

export const useHccRevisionQuery = (
  schematisationId: number | undefined,
  revisionId: number | undefined
) => useQuery(getHccRevisionQuery(schematisationId, revisionId));

export const useHccRevisionsListQuery = (schematisationId: number | undefined) =>
  useQuery(getHccRevisionsListQuery(schematisationId));

// INVALIDATION

// Patches the single revision cache and the matching entry in the revisions list cache.
export const useUpdateHccRevisionQueries = (schematisationId: number | undefined) => {
  const queryClient = useQueryClient();

  return createHccUpdateForQueries<SchematisationRevision>(queryClient, {
    getSingleQueryKey: (revisionId) => getHccRevisionQuery(schematisationId, revisionId).queryKey,
    getListQueryKey: () => getHccRevisionsListQuery(schematisationId).queryKey,
  });
};

// isValid isn't part of the partial-update response, so the latest-revision query still needs a refetch.
export const useInvalidateHccLatestRevisionQuery = (schematisationId: number | undefined) => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: getHccLatestRevisionQuery(schematisationId).queryKey,
    });
};
